import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { DataContext } from "../../../context/DataContext";
import { getEdgeRetrieveUrl } from "../../../utils/edge";
import { buildEdgeTrinsicRetrieveBody, redactRetrieveBody, isEdgeTrinsicReturn } from "./retrieveBody";

// Map URL paths to provider names
const providerMap = {
  "bankid-cz": "BANKID_CZ",
  "bankid-se": "BANKID_SE",
  "bankid-no": "BANKID_NO",
  "finnish-trust-network": "FINNISH_TRUST_NETWORK",
  "mojeid": "MOJEID",
  "trinsic": "TRINSIC",
  "brazil-cpf": "BRAZIL_CPF",
};

const TrinsicReturn = ({ providerName: propProviderName }) => {
  const { settings, data, dispatch } = useContext(DataContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState("PENDING");
  const [loading, setLoading] = useState(true);

  // Detect edge mode from data context or sessionStorage
  const storedRequestId = sessionStorage.getItem("edge_request_id");
  const isEdgeMode = isEdgeTrinsicReturn({
    dataEdge: data?.edge,
    storedRequestId,
    edgeParam: searchParams.get("edge"),
  });

  // Auto-detect provider from URL path, query param, or sessionStorage
  const getProviderName = () => {
    // Check prop first
    if (propProviderName) return propProviderName;

    // Check query param
    const providerParam = searchParams.get("provider");
    if (providerParam) return providerParam;

    // Check sessionStorage (set by TrinsicStart before redirect)
    const storedProvider = sessionStorage.getItem("trinsic_provider");
    if (storedProvider) return storedProvider;

    // Check URL path as fallback
    const path = location.pathname.toLowerCase();
    for (const [urlKey, provider] of Object.entries(providerMap)) {
      if (path.includes(urlKey)) {
        return provider;
      }
    }
    return "TRINSIC"; // Default fallback
  };

  const providerName = getProviderName();

  // Use appropriate status check based on mode
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);

      const sessionId = searchParams.get("sessionId");

      try {
        if (isEdgeMode) {
          // Edge mode - call edge retrieve endpoint directly with sessionId
          // Backend will handle the Trinsic exchange
          console.log("Edge mode detected - calling edge retrieve endpoint");

          // resultsAccessKey from the return URL goes to retrieve as `code`,
          // which is what makes it call /process/continue (see retrieveBody.js).
          const retrieveData = buildEdgeTrinsicRetrieveBody({
            storedRequestId,
            settingsRequestId: settings.request_id,
            sessionId,
            resultsAccessKey: searchParams.get("resultsAccessKey"),
          });

          console.log("Calling edge retrieve with:", redactRetrieveBody(retrieveData));

          const retrieveResponse = await fetch(getEdgeRetrieveUrl(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(retrieveData),
          });

          if (retrieveResponse.ok) {
            const retrieveResult = await retrieveResponse.json();
            console.log("Edge retrieve response:", retrieveResult);
            setStatus(retrieveResult.status || "SUCCESS");
          } else {
            console.error("Edge retrieve failed:", retrieveResponse.status);
            setStatus("ERROR");
          }
        } else {
          // Normal mode - call the exchange endpoint
          data.resultsAccessKey = searchParams.get("resultsAccessKey");
          data.code = sessionId;
          data.isIDMaxContinue = true;
          data.provider = providerName;

          const response = await fetch(
            process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data),
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data_returned = await response.json();
          console.log("Trinsic return response:", data_returned);
          setStatus(data_returned.status);
        }
      } catch (error) {
        console.error("Error fetching status:", error);
        setStatus("ERROR");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [searchParams]);

  useEffect(() => {
    // Attempt to close the current tab after verification completes
    if (status === "PENDING") {
      return;
    }
    window.close();
  }, [status]);

  return <LoadingOverlay visible />;
};

export default TrinsicReturn;
